import { useState } from 'react';
import type { Client } from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Combobox, type OptionCombobox } from '@/components/ui/combobox';
import { Encart } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { telephone } from '@/lib/format';
import { useAutocompleteClients, useFusionnerClients } from '../api/clients.api';

/**
 * Fusion manuelle des doublons — règle 12.
 *
 * L'application ne rapproche jamais deux fiches d'elle-même sur une
 * ressemblance : « ALLAMI Ihssane » et « ALLAMI Ihsane » restent séparés tant
 * que quelqu'un n'a pas tranché ici. La fusion réattache commandes, factures,
 * ventes au comptoir et paiements au client conservé, puis supprime le doublon.
 */
/**
 * « 2 commandes et 1 vente au comptoir » — n'énumère que ce qui a bougé, pour
 * que le message dise exactement ce que la fusion a fait.
 */
function resume(r: { commandes: number; factures: number; ventes: number }): string {
  const parts: string[] = [];
  if (r.commandes > 0) parts.push(`${r.commandes} commande${r.commandes > 1 ? 's' : ''}`);
  if (r.factures > 0) parts.push(`${r.factures} facture${r.factures > 1 ? 's' : ''}`);
  if (r.ventes > 0) parts.push(`${r.ventes} vente${r.ventes > 1 ? 's' : ''} au comptoir`);
  if (parts.length === 0) return 'aucun document';
  if (parts.length === 1) return parts[0] as string;
  return `${parts.slice(0, -1).join(', ')} et ${parts[parts.length - 1] as string}`;
}

export function FusionnerDialog({
  ouvert,
  clientConserve,
  onFermer,
}: {
  ouvert: boolean;
  clientConserve: Client;
  onFermer: () => void;
}) {
  const [terme, setTerme] = useState('');
  const [choisis, setChoisis] = useState<Client[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);

  const { data, isFetching } = useAutocompleteClients(terme);
  const fusionner = useFusionnerClients();
  const toast = useToast();

  const options: OptionCombobox<Client>[] = (data ?? [])
    .filter((c) => c.id !== clientConserve.id && !choisis.some((x) => x.id === c.id))
    .map((c) => ({
      cle: c.id,
      libelle: c.nom,
      description: c.tel ? telephone(c.tel) : 'Sans téléphone',
      valeur: c,
    }));

  async function valider() {
    if (choisis.length === 0) {
      setErreur('Sélectionnez au moins une fiche à fusionner.');
      return;
    }
    try {
      const resultat = await fusionner.mutateAsync({
        garde: clientConserve.id,
        mergeIds: choisis.map((c) => c.id),
      });
      toast.succes(`Fusion effectuée : ${resume(resultat)} rattaché(s) à « ${resultat.client.nom} ».`);
      setChoisis([]);
      setTerme('');
      onFermer();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'La fusion a échoué.');
    }
  }

  return (
    <Dialog
      ouvert={ouvert}
      onFermer={onFermer}
      titre="Fusionner des fiches clients"
      description={`Tout sera rattaché à « ${clientConserve.nom} ».`}
      taille="sm"
      pied={
        <>
          <Button variant="outline" onClick={onFermer}>
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={() => void valider()}
            chargement={fusionner.isPending}
            disabled={choisis.length === 0}
          >
            Fusionner
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Combobox<Client>
          label="Fiches à absorber"
          valeur={terme}
          chargement={isFetching}
          options={options}
          placeholder="Rechercher un doublon…"
          videMessage={terme.trim().length < 2 ? 'Tapez au moins deux lettres' : 'Aucun autre client'}
          onChangeTexte={setTerme}
          onSelectionner={(o) => {
            setChoisis((c) => [...c, o.valeur]);
            setTerme('');
            setErreur(null);
          }}
          erreur={erreur ?? undefined}
          aide="Vous pouvez en sélectionner plusieurs."
        />

        {choisis.length > 0 ? (
          <ul className="space-y-1">
            {choisis.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between gap-2 rounded-md border border-line px-3 py-1.5 text-sm"
              >
                <span className="min-w-0 truncate">
                  {c.nom}
                  {c.tel ? <span className="ml-2 text-ink-muted">{telephone(c.tel)}</span> : null}
                </span>
                <button
                  type="button"
                  onClick={() => setChoisis((liste) => liste.filter((x) => x.id !== c.id))}
                  className="shrink-0 text-xs font-medium text-danger hover:underline"
                >
                  Retirer
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <Encart ton="danger" titre="Action irréversible">
          Les fiches sélectionnées seront supprimées. Leurs commandes, factures, ventes au comptoir
          et paiements basculeront sur « {clientConserve.nom} ».
        </Encart>
      </div>
    </Dialog>
  );
}
